import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { userApi, ApiKeyInfo, publicApi, PricingPlan } from '../services/api'
import { 
  BarChart3, TrendingUp, AlertCircle, Zap, 
  Key, Crown, Sparkles, ArrowRight, Eye, EyeOff,
  XCircle, Calendar, Plus, Activity, RefreshCw, Star, Rocket
} from 'lucide-react'
import toast from 'react-hot-toast'
import { SubscriptionManager } from '../components/SubscriptionManager'

interface UsageSummaryData {
  monthKey: string
  totalRequests: number
  totalInputTokens: number
  totalOutputTokens: number
  totalErrors: number
  apiKeys: Array<{
    apiKeyId: string
    title: string
    isActive: boolean
    totalRequests: number
    totalInputTokens: number
    totalOutputTokens: number
    totalErrors: number
  }>
}

interface SubscriptionInfo {
  userTier: string
  subscriptionStatus: string | null
  scheduledCancellation: boolean
  scheduledChangeType: 'upgrade' | 'downgrade' | 'cancel' | null
  scheduledChangeTier: 'builder' | 'launch' | 'growth' | 'scale' | null
  scheduledChangeEffectiveDate: string | null
  paddleSubscriptionId: string | null
  subscriptionStartDate: string | null
  subscriptionEndDate: string | null
}

interface CreditInfo {
  creditBalance: number
  creditLimit: number
  creditResetDate: string
  creditsUsedThisMonth: number
  userTier: string
  scheduledChangeTier: string | null
  scheduledChangeEffectiveDate: string | null
}

export function DashboardPage() {
  const { user } = useAuth()
  const [usageData, setUsageData] = useState<UsageSummaryData | null>(null)
  const [apiKeyInfo, setApiKeyInfo] = useState<ApiKeyInfo | null>(null)
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null)
  const [creditInfo, setCreditInfo] = useState<CreditInfo | null>(null)
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([])
  const [firstName, setFirstName] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [showSubscription, setShowSubscription] = useState(false)
  const [visibleApiKeys, setVisibleApiKeys] = useState<Set<string>>(new Set())
  const [showInactiveKeys, setShowInactiveKeys] = useState(false)
  const [showInactiveUsage, setShowInactiveUsage] = useState(false)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    setLoading(true)
    try {
      const [usageSummary, apiKeys, subscription, credits, plans, profile] = await Promise.all([
        userApi.getUsageSummary().catch(() => null),
        userApi.getApiKeyInfo().catch(() => null),
        userApi.getSubscriptionStatus().catch(() => null),
        userApi.getCreditBalance().catch(() => null),
        publicApi.getPricingPlans().catch(() => []),
        userApi.getProfile().catch(() => null),
      ])
      
      setUsageData(usageSummary)
      setApiKeyInfo(apiKeys)
      setSubscriptionInfo(subscription)
      setCreditInfo(credits)
      setPricingPlans(plans)
      if (profile?.firstName) setFirstName(profile.firstName)

    } catch (error: any) {
      console.error('Failed to load dashboard data:', error)
      if (error.response?.status !== 401) {
        toast.error('Failed to load dashboard data')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await loadDashboardData()
      toast.success('Dashboard refreshed')
    } catch (error) {
      toast.error('Failed to refresh dashboard')
    } finally {
      setRefreshing(false)
    }
  }

  const toggleApiKeyVisibility = (apiKeyId: string) => {
    setVisibleApiKeys(prev => {
      const newSet = new Set(prev)
      if (newSet.has(apiKeyId)) {
        newSet.delete(apiKeyId)
      } else {
        newSet.add(apiKeyId)
      }
      return newSet
    })
  }

  const maskApiKey = (key: string) => {
    if (key.length <= 8) return '••••••••'
    return `${key.substring(0, 4)}${'•'.repeat(key.length - 8)}${key.substring(key.length - 4)}`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-12 w-12"></div>
      </div>
    )
  }

  const currentTier = subscriptionInfo?.userTier || 'free'
  const hasActiveSubscription = subscriptionInfo?.paddleSubscriptionId && subscriptionInfo?.subscriptionStatus === 'active'

  // Build tierConfig dynamically from fetched pricing plans
  const buildTierConfig = () => {
    const defaultConfig = {
      free: { name: 'Free', icon: Sparkles, color: 'from-gray-400 to-gray-500' },
      builder: { name: 'Builder', icon: Star, color: 'from-blue-400 to-blue-500' },
      launch: { name: 'Launch', icon: Zap, color: 'from-accent to-accent-2' },
      growth: { name: 'Growth', icon: Rocket, color: 'from-purple-500 to-pink-500' },
      scale: { name: 'Scale', icon: Sparkles, color: 'from-orange-500 to-red-500' },
    }

    // If pricing plans are available, use them to build config
    if (pricingPlans.length > 0) {
      const config: any = {}
      pricingPlans.forEach(plan => {
        const tierId = plan.tierId.toLowerCase()
        // Map tier to appropriate icon and color
        let icon = Sparkles
        let color = 'from-gray-400 to-gray-500'
        
        if (tierId === 'builder') {
          icon = Star
          color = 'from-blue-400 to-blue-500'
        } else if (tierId === 'launch') {
          icon = Zap
          color = 'from-accent to-accent-2'
        } else if (tierId === 'growth') {
          icon = Rocket
          color = 'from-purple-500 to-pink-500'
        } else if (tierId === 'scale') {
          icon = Sparkles
          color = 'from-orange-500 to-red-500'
        }
        
        config[tierId] = {
          name: plan.planName,
          icon: icon,
          color: color
        }
      })
      return config
    }

    // Fallback to default config if no plans fetched
    return defaultConfig
  }

  const tierConfig = buildTierConfig()
  const defaultFreeConfig = { name: 'Free', icon: Sparkles, color: 'from-gray-400 to-gray-500' }
  const currentTierConfig = tierConfig[currentTier as keyof typeof tierConfig] || tierConfig['free'] || defaultFreeConfig
  const TierIcon = currentTierConfig.icon

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header with Tier Badge */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-text mb-2">
            Welcome back, <span className="text-gradient">{firstName || user?.signInDetails?.loginId?.split('@')[0] || 'User'}</span>
          </h1>
          <p className="text-text-muted">
            Here's your complete overview
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-bg hover:bg-border border border-border text-text font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh dashboard"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          {currentTier !== 'free' && currentTierConfig && (
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r ${currentTierConfig.color} text-white shadow-lg`}>
              <TierIcon className="h-5 w-5" />
              <span className="font-semibold">{currentTierConfig.name} Plan</span>
            </div>
          )}
        </div>
      </div>

      {/* Scheduled Change Alert */}
      {subscriptionInfo?.scheduledChangeType && (
        <div className="p-4 rounded-lg bg-warning/5 border border-warning/30 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-text mb-1">
              {subscriptionInfo.scheduledChangeType === 'cancel' && 'Subscription Ending Soon'}
              {subscriptionInfo.scheduledChangeType === 'downgrade' && 'Downgrade Scheduled'}
              {subscriptionInfo.scheduledChangeType === 'upgrade' && 'Upgrade Scheduled'}
            </p>
            <p className="text-xs text-text-muted">
              {subscriptionInfo.scheduledChangeType === 'cancel' && (
                <>Your subscription ends on {new Date(subscriptionInfo.scheduledChangeEffectiveDate!).toLocaleDateString()}. You'll switch to the Free plan.</>
              )}
              {subscriptionInfo.scheduledChangeType === 'downgrade' && (
                <>Changes to {subscriptionInfo.scheduledChangeTier?.toUpperCase()} on {new Date(subscriptionInfo.scheduledChangeEffectiveDate!).toLocaleDateString()}.</>
              )}
              {subscriptionInfo.scheduledChangeType === 'upgrade' && (
                <>Changes to {subscriptionInfo.scheduledChangeTier?.toUpperCase()} on {new Date(subscriptionInfo.scheduledChangeEffectiveDate!).toLocaleDateString()}.</>
              )}
            </p>
          </div>
          <button
            onClick={() => setShowSubscription(true)}
            className="text-xs font-medium text-warning hover:underline whitespace-nowrap"
          >
            Manage
          </button>
        </div>
      )}

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Stats & Usage */}
        <div className="lg:col-span-2 space-y-6">

          {/* Free tier upgrade CTA */}
          {currentTier === 'free' && (
            <div className="card p-8 flex flex-col items-center text-center bg-gradient-to-br from-accent/5 to-accent-2/5 border-accent/20">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent to-accent-2 flex items-center justify-center mb-4 shadow-lg shadow-accent/20">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-xl font-bold text-text mb-2">Start building with AI</h2>
              <p className="text-text-muted text-sm mb-6 max-w-sm">
                Upgrade to a paid plan to get an API key and start making AI requests. Choose the plan that fits your needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="/pricing"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-accent to-accent-2 hover:from-accent/90 hover:to-accent-2/90 text-white rounded-lg font-semibold transition-all duration-200 shadow-lg shadow-accent/20"
                >
                  <Crown className="h-4 w-4" />
                  View Plans
                </a>
                <a
                  href="/dashboard/documentation"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-transparent border border-border hover:bg-panel text-text rounded-lg font-medium transition-all duration-200"
                >
                  <ArrowRight className="h-4 w-4" />
                  Documentation
                </a>
              </div>
            </div>
          )}

          {/* Credit Usage Card - paid users only */}
          {currentTier !== 'free' && creditInfo && (
            <div className="card p-4 bg-gradient-to-br from-accent-2/5 to-accent/5 border-accent-2/20">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-2 rounded-lg bg-accent-2/20">
                  <Activity className="h-5 w-5 text-accent-2" />
                </div>
                <span className="text-sm font-semibold text-text">Used This Month</span>
              </div>
              <p className="text-3xl font-bold text-text mb-1">
                {creditInfo.creditsUsedThisMonth.toLocaleString()}
              </p>
              <div className="flex items-center justify-between text-xs text-text-muted">
                <span>credits consumed</span>
                <span>{Math.round((creditInfo.creditsUsedThisMonth / creditInfo.creditLimit) * 100)}%</span>
              </div>
              <div className="mt-2 h-2 bg-bg rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-accent-2 to-accent transition-all duration-500"
                  style={{ width: `${Math.min((creditInfo.creditsUsedThisMonth / creditInfo.creditLimit) * 100, 100)}%` }}
                />
              </div>
              <p className="text-xs text-text-muted mt-2">
                {(creditInfo.creditLimit - creditInfo.creditsUsedThisMonth).toLocaleString()} remaining
              </p>
            </div>
          )}

          {/* Key Metrics - paid users only */}
          {/* {currentTier !== 'free' && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="card p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-2 rounded-lg bg-accent/10">
                  <BarChart3 className="h-4 w-4 text-accent" />
                </div>
              </div>
              <p className="text-xs text-text-muted mb-1">Requests This Month</p>
              <p className="text-2xl font-bold text-text">
                {usageData?.totalRequests?.toLocaleString() || '0'}
              </p>
            </div>

            <div className="card p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-2 rounded-lg bg-accent-2/10">
                  <TrendingUp className="h-4 w-4 text-accent-2" />
                </div>
              </div>
              <p className="text-xs text-text-muted mb-1">Input Tokens</p>
              <p className="text-2xl font-bold text-text">
                {usageData?.totalInputTokens?.toLocaleString() || '0'}
              </p>
            </div>

            <div className="card p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-2 rounded-lg bg-success/10">
                  <TrendingDown className="h-4 w-4 text-success" />
                </div>
              </div>
              <p className="text-xs text-text-muted mb-1">Output Tokens</p>
              <p className="text-2xl font-bold text-text">
                {usageData?.totalOutputTokens?.toLocaleString() || '0'}
              </p>
            </div>
          </div> )} */}
           {/* end paid-only key metrics */}

          {/* Monthly Summary - paid users only */}
          {currentTier !== 'free' && (
          <div className="card">
            <div className="p-4 border-b border-border">
              <h3 className="text-lg font-semibold text-text">Monthly Summary</h3>
              <p className="text-xs text-text-muted mt-1">Usage for {usageData?.monthKey || 'current month'}</p>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-bg border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart3 className="h-4 w-4 text-accent" />
                    <p className="text-xs text-text-muted">Total Requests</p>
                  </div>
                  <p className="text-2xl font-bold text-text">
                    {usageData?.totalRequests?.toLocaleString() || '0'}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-bg border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-4 w-4 text-accent-2" />
                    <p className="text-xs text-text-muted">Total Tokens</p>
                  </div>
                  <p className="text-2xl font-bold text-text">
                    {((usageData?.totalInputTokens || 0) + (usageData?.totalOutputTokens || 0)).toLocaleString()}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-bg border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="h-4 w-4 text-error" />
                    <p className="text-xs text-text-muted">Errors</p>
                  </div>
                  <p className="text-2xl font-bold text-text">
                    {usageData?.totalErrors?.toLocaleString() || '0'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          )} {/* end paid-only monthly summary */}

          {/* API Keys Usage Breakdown - paid users only */}
          {currentTier !== 'free' && usageData?.apiKeys && usageData.apiKeys.length > 0 && (
            <div className="card">
              <div className="p-4 border-b border-border">
                <h3 className="text-lg font-semibold text-text flex items-center gap-2">
                  <Key className="h-5 w-5 text-accent-2" />
                  Usage by API Key
                </h3>
                <p className="text-xs text-text-muted mt-1">Breakdown per API key</p>
              </div>
              <div className="p-4 space-y-3">
                {/* Active Keys Usage */}
                {usageData.apiKeys
                  .filter(apiKey => apiKey.isActive)
                  .map((apiKey) => (
                    <div key={apiKey.apiKeyId} className="p-4 rounded-lg bg-bg border border-border">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent to-accent-2 flex items-center justify-center flex-shrink-0">
                            <Key className="h-4 w-4 text-white" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-text truncate">
                              {apiKey.title || 'Untitled Key'}
                            </p>
                            <p className="text-xs text-text-muted truncate">{apiKey.apiKeyId}</p>
                          </div>
                        </div>
                        <div className="badge-success flex-shrink-0">
                          Active
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div>
                          <p className="text-xs text-text-muted mb-1">Requests</p>
                          <p className="text-lg font-bold text-text">{apiKey.totalRequests.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-text-muted mb-1">Input</p>
                          <p className="text-lg font-bold text-text">{apiKey.totalInputTokens.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-text-muted mb-1">Output</p>
                          <p className="text-lg font-bold text-text">{apiKey.totalOutputTokens.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-text-muted mb-1">Errors</p>
                          <p className="text-lg font-bold text-text">{apiKey.totalErrors.toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}

                {/* No Active Keys Message */}
                {usageData.apiKeys.filter(apiKey => apiKey.isActive).length === 0 && (
                  <div className="text-center py-4">
                    <p className="text-sm text-text-muted">No active API keys with usage</p>
                  </div>
                )}

                {/* Inactive Keys Toggle */}
                {usageData.apiKeys.filter(apiKey => !apiKey.isActive).length > 0 && (
                  <div>
                    <button
                      onClick={() => setShowInactiveUsage(!showInactiveUsage)}
                      className="w-full px-3 py-2 text-xs font-medium text-text-muted hover:text-text bg-bg hover:bg-border border border-border rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                    >
                      {showInactiveUsage ? (
                        <>
                          <EyeOff className="h-3 w-3" />
                          Hide Inactive Keys ({usageData.apiKeys.filter(apiKey => !apiKey.isActive).length})
                        </>
                      ) : (
                        <>
                          <Eye className="h-3 w-3" />
                          Show Inactive Keys ({usageData.apiKeys.filter(apiKey => !apiKey.isActive).length})
                        </>
                      )}
                    </button>

                    {/* Inactive Keys Usage */}
                    {showInactiveUsage && (
                      <div className="space-y-3 mt-3">
                        {usageData.apiKeys
                          .filter(apiKey => !apiKey.isActive)
                          .map((apiKey) => (
                            <div key={apiKey.apiKeyId} className="p-4 rounded-lg bg-bg border border-border opacity-60">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center flex-shrink-0">
                                    <Key className="h-4 w-4 text-white" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-sm font-semibold text-text truncate">
                                      {apiKey.title || 'Untitled Key'}
                                    </p>
                                    <p className="text-xs text-text-muted truncate">{apiKey.apiKeyId}</p>
                                  </div>
                                </div>
                                <div className="badge-error flex-shrink-0">
                                  Inactive
                                </div>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <div>
                                  <p className="text-xs text-text-muted mb-1">Requests</p>
                                  <p className="text-lg font-bold text-text">{apiKey.totalRequests.toLocaleString()}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-text-muted mb-1">Input</p>
                                  <p className="text-lg font-bold text-text">{apiKey.totalInputTokens.toLocaleString()}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-text-muted mb-1">Output</p>
                                  <p className="text-lg font-bold text-text">{apiKey.totalOutputTokens.toLocaleString()}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-text-muted mb-1">Errors</p>
                                  <p className="text-lg font-bold text-text">{apiKey.totalErrors.toLocaleString()}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Account & Actions */}
        <div className="space-y-6">
          {/* Account Overview */}
          <div className="card">
            <div className="p-4 border-b border-border">
              <h3 className="text-lg font-semibold text-text">Account</h3>
            </div>
            <div className="p-4 space-y-4">
              {/* Subscription Status */}
              <div className="p-4 rounded-lg bg-bg border border-border">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <TierIcon className="h-5 w-5 text-accent" />
                    <span className="text-sm font-semibold text-text">{currentTierConfig.name}</span>
                  </div>
                  {hasActiveSubscription && (
                    <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-success/10 border border-success/20">
                      <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                      <span className="text-xs font-medium text-success">Active</span>
                    </div>
                  )}
                </div>
                {subscriptionInfo?.subscriptionEndDate && (
                  <div className="flex items-center gap-2 text-xs text-text-muted">
                    <Calendar className="h-3 w-3" />
                    <span>
                      {subscriptionInfo.scheduledCancellation ? 'Until' : 'Renews'} {new Date(subscriptionInfo.subscriptionEndDate).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Manage Subscription Button */}
              <button
                onClick={() => setShowSubscription(!showSubscription)}
                className="w-full px-4 py-2 bg-accent/10 hover:bg-accent/20 text-accent border border-accent/30 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Crown className="h-4 w-4" />
                <span>{showSubscription ? 'Hide' : 'Manage'} Subscription</span>
              </button>
            </div>
          </div>

          {/* Subscription Manager (Collapsible) */}
          {showSubscription && subscriptionInfo && (
            <div className="card">
              <div className="p-4 border-b border-border">
                <h3 className="text-lg font-semibold text-text">Subscription</h3>
              </div>
              <div className="p-4">
                <SubscriptionManager 
                  subscriptionInfo={subscriptionInfo as any} 
                  onRefresh={loadDashboardData}
                  pricingPlans={pricingPlans}
                />
              </div>
            </div>
          )}

          {/* API Keys Quick View */}
          <div className="card">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-text">API Keys</h3>
              <a 
                href="/dashboard/api-keys"
                className="text-xs font-medium text-accent hover:underline flex items-center gap-1"
              >
                Manage
                <ArrowRight className="h-3 w-3" />
              </a>
            </div>
            <div className="p-4">
              {apiKeyInfo?.apiKeys && apiKeyInfo.apiKeys.length > 0 ? (
                <div className="space-y-3">
                  {/* Active Keys */}
                  <div className="space-y-2">
                    {apiKeyInfo.apiKeys
                      .filter(key => key.isActive)
                      .slice(0, 3)
                      .map((key) => (
                        <div key={key.apiKeyId} className="p-3 rounded-lg bg-bg border border-border">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-semibold text-text truncate flex-1">
                              {key.title || 'Untitled'}
                            </p>
                            <div className="badge-success text-xs">
                              Active
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-text-muted font-mono flex-1 truncate">
                              {visibleApiKeys.has(key.apiKeyId) ? key.apiKeyId : maskApiKey(key.apiKeyId)}
                            </p>
                            <button
                              onClick={() => toggleApiKeyVisibility(key.apiKeyId)}
                              className="p-1 hover:bg-border rounded transition-colors"
                            >
                              {visibleApiKeys.has(key.apiKeyId) ? (
                                <EyeOff className="h-3 w-3 text-text-muted" />
                              ) : (
                                <Eye className="h-3 w-3 text-text-muted" />
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                    
                    {apiKeyInfo.apiKeys.filter(key => key.isActive).length === 0 && (
                      <div className="text-center py-4">
                        <p className="text-sm text-text-muted">No active API keys</p>
                      </div>
                    )}
                  </div>

                  {/* Inactive Keys Toggle */}
                  {apiKeyInfo.apiKeys.filter(key => !key.isActive).length > 0 && (
                    <div>
                      <button
                        onClick={() => setShowInactiveKeys(!showInactiveKeys)}
                        className="w-full px-3 py-2 text-xs font-medium text-text-muted hover:text-text bg-bg hover:bg-border border border-border rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                      >
                        {showInactiveKeys ? (
                          <>
                            <EyeOff className="h-3 w-3" />
                            Hide Inactive ({apiKeyInfo.apiKeys.filter(key => !key.isActive).length})
                          </>
                        ) : (
                          <>
                            <Eye className="h-3 w-3" />
                            Show Inactive ({apiKeyInfo.apiKeys.filter(key => !key.isActive).length})
                          </>
                        )}
                      </button>

                      {/* Inactive Keys List */}
                      {showInactiveKeys && (
                        <div className="space-y-2 mt-2">
                          {apiKeyInfo.apiKeys
                            .filter(key => !key.isActive)
                            .map((key) => (
                              <div key={key.apiKeyId} className="p-3 rounded-lg bg-bg border border-border opacity-60">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-sm font-semibold text-text truncate flex-1">
                                    {key.title || 'Untitled'}
                                  </p>
                                  <div className="badge-error text-xs">
                                    Inactive
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <p className="text-xs text-text-muted font-mono flex-1 truncate">
                                    {visibleApiKeys.has(key.apiKeyId) ? key.apiKeyId : maskApiKey(key.apiKeyId)}
                                  </p>
                                  <button
                                    onClick={() => toggleApiKeyVisibility(key.apiKeyId)}
                                    className="p-1 hover:bg-border rounded transition-colors"
                                  >
                                    {visibleApiKeys.has(key.apiKeyId) ? (
                                      <EyeOff className="h-3 w-3 text-text-muted" />
                                    ) : (
                                      <Eye className="h-3 w-3 text-text-muted" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Show more active keys indicator */}
                  {apiKeyInfo.apiKeys.filter(key => key.isActive).length > 3 && (
                    <p className="text-xs text-text-muted text-center pt-1">
                      +{apiKeyInfo.apiKeys.filter(key => key.isActive).length - 3} more active
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Key className="h-10 w-10 text-text-muted mx-auto mb-2 opacity-50" />
                  <p className="text-sm text-text-muted mb-3">No API keys yet</p>
                  <a
                    href="/dashboard/api-keys"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent/90 text-white rounded-lg font-medium transition-all duration-200"
                  >
                    <Plus className="h-4 w-4" />
                    Create API Key
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <div className="p-4 border-b border-border">
              <h3 className="text-lg font-semibold text-text">Quick Actions</h3>
            </div>
            <div className="p-4 space-y-2">
              <a 
                href="/dashboard/api-keys" 
                className="flex items-center gap-3 p-3 rounded-lg bg-bg border border-border hover:border-accent/50 transition-all duration-200 group"
              >
                <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                  <Key className="h-4 w-4 text-accent" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-text group-hover:text-accent transition-colors">API Keys</p>
                  <p className="text-xs text-text-muted">Manage your keys</p>
                </div>
                <ArrowRight className="h-4 w-4 text-text-muted group-hover:text-accent transition-colors" />
              </a>
              
              <a 
                href="/pricing" 
                className="flex items-center gap-3 p-3 rounded-lg bg-bg border border-border hover:border-accent-2/50 transition-all duration-200 group"
              >
                <div className="w-8 h-8 rounded-lg bg-accent-2/10 flex items-center justify-center group-hover:bg-accent-2/20 transition-colors">
                  <TrendingUp className="h-4 w-4 text-accent-2" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-text group-hover:text-accent-2 transition-colors">Upgrade</p>
                  <p className="text-xs text-text-muted">View pricing plans</p>
                </div>
                <ArrowRight className="h-4 w-4 text-text-muted group-hover:text-accent-2 transition-colors" />
              </a>
              
              <a 
                href="/dashboard/profile" 
                className="flex items-center gap-3 p-3 rounded-lg bg-bg border border-border hover:border-accent/50 transition-all duration-200 group"
              >
                <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                  <Activity className="h-4 w-4 text-accent" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-text group-hover:text-accent transition-colors">Profile</p>
                  <p className="text-xs text-text-muted">Update settings</p>
                </div>
                <ArrowRight className="h-4 w-4 text-text-muted group-hover:text-accent transition-colors" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
